import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';

import Chip from '../brand/Chip';
import { media } from '../../styles/style-utils';
import { type } from '../../styles/typography';
import whereverApp from '../../assets/images/home/wherever-app.jpg';
import whatever101Still from '../../assets/images/home/whatever-101-still.png';

const WHATEVER_VIDEO_SRC = '/videos/whatever-101.mp4';

const Wrapper = styled.section`
  --tile-peek: 64px;
  display: flex;
  gap: 0;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  padding: 0;
  scroll-padding: 0;
  scrollbar-width: none;
  background-color: var(--b-black);

  &::-webkit-scrollbar {
    display: none;
  }

  ${media.medium`
    display: grid;
    grid-template-columns: 1fr 1fr;
    overflow: visible;
    padding-right: 0;
    scroll-snap-type: none;
  `};
`;

const Tile = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: calc(100vw - var(--tile-peek));
  height: 340px;
  overflow: hidden;
  color: var(--b-white);
  scroll-snap-align: start;
  scroll-snap-stop: always;

  ${media.medium`
    width: auto;
    height: 420px;
    scroll-snap-align: none;
    scroll-snap-stop: normal;
  `};

  ${media.large`
    height: 502px;
  `};
`;

const Caption = styled.div`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;

  ${media.medium`
    left: var(--b-gutter);
    right: 32px;
    bottom: 34px;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
  `};
`;

const Headline = styled.h3`
  ${type('headline', 'small', { emphasis: true })};
  margin: 0;
  padding: 0;
  color: var(--b-white);
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;

  ${Chip} {
    height: auto;
    min-height: 28px;
    padding: 4px 10px;
    white-space: normal;
  }

  ${media.medium`
    flex: 0 0 auto;
    flex-wrap: nowrap;
    justify-content: flex-end;
    gap: 3px;

    ${Chip} {
      height: 36px;
      min-height: 0;
      padding: 0 15px;
      white-space: nowrap;
    }
  `};
`;

const Wherever = styled(Tile)`
  background-color: var(--b-ink);

  > img {
    position: absolute;
    width: 300%;
    max-width: none;
    height: auto;
    left: 35%;
    top: 40%;
    transform: translate(-35%, -40%);
  }
`;

const Whatever = styled(Tile)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--b-coal);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 1;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.72), rgba(0, 0, 0, 0) 58%);
    pointer-events: none;
  }

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 70%;
    object-fit: cover;
  }

  ${media.medium`
    /* Two lines of the headline, plus its offset from the bottom. */
    padding-bottom: calc(var(--size-500) * 1.15 * 2 + 34px);

    &::after {
      content: none;
    }

    video {
      position: static;
      width: 100%;
      max-height: 100%;
      height: auto;
      object-fit: contain;
      aspect-ratio: 540 / 190;
    }
  `};
`;

const Tiles = ({ wherever, whatever, whereverChips, whateverChips }) => (
  <Wrapper>
    <Wherever>
      <img src={whereverApp} alt="" />
      <Caption>
        <Headline>
          {wherever || (
            <FormattedMessage
              id="home.tiles.wherever"
              defaultMessage="Wherever{br}you are"
              values={{ br: <br /> }}
            />
          )}
        </Headline>
        <Chips>
          {whereverChips || (
            <React.Fragment>
              <Chip tone="white" pill>
                <FormattedMessage id="home.tiles.on-the-go" defaultMessage="On the Go" />
              </Chip>
              <Chip tone="dark">
                <FormattedMessage id="home.tiles.at-home" defaultMessage="At Home" />
              </Chip>
            </React.Fragment>
          )}
        </Chips>
      </Caption>
    </Wherever>
    <Whatever>
      <video src={WHATEVER_VIDEO_SRC} poster={whatever101Still} autoPlay muted loop playsInline />
      <Caption>
        <Headline>
          {whatever || (
            <FormattedMessage
              id="home.tiles.whatever"
              defaultMessage="Whatever{br}you love"
              values={{ br: <br /> }}
            />
          )}
        </Headline>
        <Chips>
          {whateverChips || (
            <Chip tone="dark">
              <FormattedMessage id="home.tiles.tracks" defaultMessage="2.5M+ Tracks" />
            </Chip>
          )}
        </Chips>
      </Caption>
    </Whatever>
  </Wrapper>
);

Tiles.propTypes = {
  wherever: PropTypes.node,
  whatever: PropTypes.node,
  whereverChips: PropTypes.node,
  whateverChips: PropTypes.node,
};

export default Tiles;



// WEBPACK FOOTER //
// /tmp/build_2cb81483/src/components/home/Tiles.js